import React, { useState, useEffect } from 'react';
import { BOOKING_STATUSES, USER_ROLES } from '../../constants/statuses';
import Button from '../common/Button';
import ReviewModal from './ReviewModal';

const ReviewButton = ({ booking, currentUserId }) => {
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'view'
  const [reviewIdToView, setReviewIdToView] = useState(null);

  // Check if reviews plugin is enabled from localized data
  const reviewsEnabled = window.wwoofBooking?.plugins?.reviewsEnabled || false;
  
  // If reviews plugin is not enabled, don't render anything
  if (!reviewsEnabled) {
    return null;
  }

  const isHost = parseInt(booking.host_id) === parseInt(currentUserId);
  const isWwoofer = parseInt(booking.wwoofer_id) === parseInt(currentUserId);

  // Local, mutable review id state so UI can update without page reload
  const [currentUserReviewId, setCurrentUserReviewId] = useState(
    isHost ? booking.host_review_id : booking.wwoofer_review_id
  );
  const [otherUserReviewId, setOtherUserReviewId] = useState(
    isHost ? booking.wwoofer_review_id : booking.host_review_id
  );

  // Keep local state in sync if booking prop changes
  useEffect(() => {
    setCurrentUserReviewId(isHost ? booking.host_review_id : booking.wwoofer_review_id);
    setOtherUserReviewId(isHost ? booking.wwoofer_review_id : booking.host_review_id);
  }, [booking, isHost]);

  // Only show review options for completed bookings
  if (booking.status !== BOOKING_STATUSES.COMPLETED) {
    return null;
  }
  
  // User must be either host or wwoofer
  if (!isHost && !isWwoofer) {
    return null;
  }

  // Determine review states
  // Use the local state values for rendering
  const hasLeftReview = currentUserReviewId && currentUserReviewId !== '0';
  const otherUserHasLeftReview = otherUserReviewId && otherUserReviewId !== '0';

  const handleLeaveReview = () => {
    setModalMode('create');
    setShowReviewModal(true);
  };

  const handleViewMyReview = () => {
    setModalMode('view');
  setReviewIdToView(currentUserReviewId);
    setShowReviewModal(true);
  };

  const handleViewOtherReview = () => {
    setModalMode('view');
    setReviewIdToView(otherUserReviewId);
    setShowReviewModal(true);
  };

  // Callback passed to modal to update state immediately after a successful submit
  const handleReviewSubmitted = (newReviewId) => {
    // If newReviewId is provided, store it; otherwise mark as a non-zero placeholder
    const idToSet = newReviewId ?? '1';
    setCurrentUserReviewId(idToSet);

    // Optionally also update booking object fields if parent relies on it elsewhere
    // (we don't mutate props directly here)
  };

  return (
    <div className="review-buttons">
      {!hasLeftReview && (
        <Button
          onClick={handleLeaveReview}
          className="review-button leave-review"
        >
          ⭐ Leave Review
        </Button>
      )}

      {hasLeftReview && (
        <Button
          onClick={handleViewMyReview}
          className="review-button view-my-review"
          variant="secondary"
        >
          📝 View My Review
        </Button>
      )}

      {otherUserHasLeftReview && (
        <Button
          onClick={handleViewOtherReview}
          className="review-button view-other-review"
          variant="secondary"
        >
          👁️ View the {isHost ? "Host's" : "WWOOFer's"} Review
        </Button>
      )}

      {showReviewModal && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          mode={modalMode}
          booking={booking}
          currentUserId={currentUserId}
          reviewId={reviewIdToView}
          onReviewSubmitted={handleReviewSubmitted}
        />
      )}
    </div>
  );
};

export default ReviewButton;
